import { useEffect, useState } from "react";
import { Plug, RefreshCw, CheckCircle2, AlertTriangle, Server } from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";

/** Outlook bridge smoke-test page.
 *
 *  Tests the live connection from this brain to the WakiOutlookBridge
 *  VSTO add-in running inside Outlook in the Parallels VM. The brain
 *  proxies a single GET to the bridge's /helloworld endpoint and
 *  surfaces the resulting snapshot (account, store, inbox count,
 *  calendar count for next 30 days, host metadata) as a clean card.
 *
 *  No raw JSON dumped on screen - per the visual-polish memory the
 *  payload is unpacked into labelled rows. The raw JSON is available
 *  via the toggle for debugging only. */
type BridgeSnapshot = {
  message: string;
  outlook_version: string;
  current_user_smtp: string;
  current_user_display_name: string;
  default_store_name: string;
  mail_count_inbox: number;
  calendar_count_30d: number;
  timezone: string;
  vm_hostname: string;
  timestamp_utc: string;
};

type ProxyResponse = {
  ok: boolean;
  status?: number;
  url: string;
  body?: BridgeSnapshot | string | null;
  error?: string;
  message?: string;
  hint?: string;
};

type UrlInfo = { url: string; env_set: boolean };

function fmtNumber(n: number): string {
  if (n < 0) return "—";
  return n.toLocaleString();
}

function fmtRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const ago = Math.max(0, Date.now() - d.getTime());
    if (ago < 60_000) return `${Math.round(ago / 1000)}s ago`;
    if (ago < 3_600_000) return `${Math.round(ago / 60_000)}m ago`;
    return d.toLocaleTimeString();
  } catch {
    return iso;
  }
}

export function OutlookBridgeTest() {
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState<ProxyResponse | null>(null);
  const [urlInfo, setUrlInfo] = useState<UrlInfo | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    fetch("/api/bridge/url", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setUrlInfo(j))
      .catch(() => undefined);
  }, []);

  const test = async () => {
    if (busy) return;
    setBusy(true);
    setResp(null);
    setShowRaw(false);
    const proxyPath = "/api/bridge/helloworld";
    try {
      const r = await fetch(proxyPath, { credentials: "same-origin" });
      const text = await r.text();
      let parsed: unknown = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (parsed && typeof parsed === "object") {
        setResp(parsed as ProxyResponse);
      } else {
        // Non-JSON body (HTML error page, plain text, etc). Surface what we
        // actually got so the user isn't left guessing.
        const preview = text.length > 240 ? text.slice(0, 240) + "..." : text;
        setResp({
          ok: false,
          status: r.status,
          url: proxyPath,
          error: "non_json_response",
          message: `Proxy returned ${r.status} with non-JSON body. First bytes: ${preview}`,
          hint:
            r.status >= 500
              ? "Likely a transient Cloudflare tunnel hiccup or daemon error. Try again."
              : r.status === 401
                ? "Session expired. Sign in again."
                : "Inspect the response body above; the daemon proxy normally returns JSON.",
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setResp({
        ok: false,
        url: proxyPath,
        error: "fetch_failed",
        message: msg,
      });
    } finally {
      setBusy(false);
    }
  };

  const snapshot: BridgeSnapshot | null =
    resp?.ok && resp.body && typeof resp.body === "object"
      ? (resp.body as BridgeSnapshot)
      : null;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Outlook bridge
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Tests the live connection to your Outlook bridge in Parallels.
          Shows current account, inbox count, and calendar count.
        </p>
      </header>

      <TeachingBanner id="outlook_bridge_test_overview" />

      <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 sm:p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white shadow-md shrink-0">
            <Plug className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              WakiOutlookBridge
            </h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Plain-HTTP COM bridge running inside Outlook (Classic) on Windows.
            </p>
            {urlInfo && (
              <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <Server className="w-3 h-3" />
                <code className="font-mono text-slate-700 dark:text-slate-300">{urlInfo.url}</code>
                <span className="text-slate-400 dark:text-slate-500">
                  ({urlInfo.env_set ? "from OUTLOOK_BRIDGE_URL" : "default"})
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={test}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Testing…" : "Test bridge"}
          </button>
        </div>

        {resp && resp.ok && snapshot && (
          <div className="rounded-xl border border-emerald-300/50 dark:border-emerald-700/40 bg-emerald-50/70 dark:bg-emerald-900/20 px-4 py-3 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Bridge reachable</span>
              <span className="text-[11px] text-emerald-600/70 dark:text-emerald-400/70 ml-auto">
                {fmtRelative(snapshot.timestamp_utc)}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Account" value={snapshot.current_user_display_name} />
              <Field label="Email" value={snapshot.current_user_smtp} mono />
              <Field label="Default store" value={snapshot.default_store_name} />
              <Field label="Outlook version" value={snapshot.outlook_version} mono />
              <Field label="Inbox count" value={fmtNumber(snapshot.mail_count_inbox)} accent />
              <Field label="Calendar (next 30d)" value={fmtNumber(snapshot.calendar_count_30d)} accent />
              <Field label="VM hostname" value={snapshot.vm_hostname} mono />
              <Field label="Timezone" value={snapshot.timezone} mono />
            </div>
          </div>
        )}

        {resp && !resp.ok && (
          <div className="rounded-xl border border-rose-300/50 dark:border-rose-700/40 bg-rose-50/70 dark:bg-rose-900/20 px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-semibold">Bridge unreachable</span>
            </div>
            <p className="text-xs text-rose-700/90 dark:text-rose-300/90">
              {resp.message ?? resp.error ?? "Unknown error."}
            </p>
            {resp.hint && (
              <p className="text-xs text-rose-600/70 dark:text-rose-400/70">{resp.hint}</p>
            )}
            <p className="text-[11px] font-mono text-rose-700/70 dark:text-rose-300/70 break-all">
              {resp.url}
            </p>
          </div>
        )}

        {resp && (
          <div className="pt-1">
            <button
              type="button"
              className="text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? "Hide" : "Show"} raw response
            </button>
            {showRaw && (
              <pre className="mt-2 text-[11px] font-mono text-slate-700 dark:text-slate-300 bg-slate-100/60 dark:bg-slate-800/40 rounded-lg p-3 overflow-x-auto">
                {JSON.stringify(resp, null, 2)}
              </pre>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, mono, accent }: { label: string; value: string; mono?: boolean; accent?: boolean }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div
        className={[
          "text-sm",
          mono ? "font-mono" : "font-medium",
          accent
            ? "text-sky-700 dark:text-sky-300 text-base font-bold"
            : "text-slate-800 dark:text-slate-200",
        ].join(" ")}
      >
        {value}
      </div>
    </div>
  );
}
